import { useGdprRequests, type GdprRequest } from '../../api/queries.js';
import { useAuthStore } from '../../auth/auth-store.js';
import {
  Button,
  type Column,
  DataTable,
  Field,
  PageHeader,
  Select,
  Textarea,
  Toggle,
  formatDate,
  timeAgo,
} from '../../components/ui/index.js';

const STATUS_COLOR: Record<string, string> = {
  pending: 'warning',
  in_review: 'info',
  approved: 'info',
  completed: 'success',
  rejected: 'danger',
};
const KIND_LABEL: Record<string, string> = {
  erasure: 'Удаление',
  export: 'Экспорт',
  rectification: 'Исправление',
  objection: 'Возражение',
};

export function SettingsGdpr(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const slug = user?.workspaceSlug;
  const reqQ = useGdprRequests(slug);

  const columns: Column<GdprRequest>[] = [
    { key: 'email', label: 'Запросил', render: (r) => r.requesterEmail ?? '—' },
    { key: 'kind', label: 'Тип', width: 130, render: (r) => KIND_LABEL[r.kind] ?? r.kind },
    {
      key: 'status',
      label: 'Статус',
      width: 130,
      render: (r) => (
        <span className={`inbx-pill-badge inbx-pill-badge--${STATUS_COLOR[r.status] ?? 'default'}`}>
          {r.status}
        </span>
      ),
    },
    { key: 'created', label: 'Создан', width: 130, render: (r) => timeAgo(r.createdAt) },
    {
      key: 'scheduled',
      label: 'Срок',
      width: 130,
      render: (r) => (r.scheduledFor ? formatDate(r.scheduledFor) : '—'),
    },
    {
      key: 'actions',
      label: '',
      width: 160,
      render: (r) =>
        r.status === 'pending' || r.status === 'in_review' ? (
          <Button size="sm" variant="primary">
            Выполнить
          </Button>
        ) : r.status === 'completed' && r.kind === 'export' ? (
          <Button size="sm" variant="secondary" icon="ti-download">
            Скачать
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="inbx-page">
      <PageHeader title="GDPR" />
      <div className="inbx-page__body" style={{ maxWidth: 1000 }}>
        <div className="inbx-card">
          <h4 className="inbx-card__title">Запросы на удаление / экспорт</h4>
          <DataTable rows={reqQ.data ?? []} columns={columns} loading={reqQ.isLoading} />
        </div>

        <div className="inbx-card">
          <h4 className="inbx-card__title">Настройки хранения данных</h4>
          <Field label="Автоудаление решённых тикетов">
            <Select defaultValue="never">
              <option value="never">никогда</option>
              <option value="30">30 дней</option>
              <option value="90">90 дней</option>
              <option value="180">180 дней</option>
              <option value="365">365 дней</option>
            </Select>
          </Field>
          <Field label="Pending тикеты старше N дней">
            <Select defaultValue="90">
              <option value="30">30</option>
              <option value="90">90</option>
              <option value="180">180</option>
            </Select>
          </Field>
          <Field label="Медиа-вложения (дней)">
            <Select defaultValue="365">
              <option value="90">90</option>
              <option value="180">180</option>
              <option value="365">365</option>
            </Select>
          </Field>
          <Button variant="primary">Сохранить</Button>
        </div>

        <div className="inbx-card">
          <h4 className="inbx-card__title">Согласия</h4>
          <Toggle
            checked={false}
            onChange={() => {}}
            label="Показывать чекбокс согласия в виджете"
          />
          <Field label="Текст согласия">
            <Textarea
              rows={3}
              defaultValue="Нажимая кнопку, вы соглашаетесь с условиями обработки персональных данных."
            />
          </Field>
        </div>

        <div className="inbx-card">
          <h4 className="inbx-card__title">Документы</h4>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button variant="secondary" icon="ti-file-download">
              DPA (PDF)
            </Button>
            <Button variant="secondary" icon="ti-file-download">
              Политика конфиденциальности
            </Button>
            <Button variant="secondary" icon="ti-file-download">
              Sub-processor list
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
